import React from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import PanToolIcon from "@mui/icons-material/PanTool";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useStatusPalette } from "../../../utils/statusColors";
import { STATUS_HUMAN_TAKEOVER } from "./conversationConstants";
import {
  buildStatusConfig,
  buildSessionEmailAddress,
  formatShareId,
} from "./conversationUtils";
import { pageHeaderMinSx } from "../../../constants/pageLayout";
import { iconBtnSx } from "../ChatHeaderActions";
import { StatusBadge } from "../SessionRow";

function SessionEmailButton({ email }) {
  const _sp = useStatusPalette();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const closeTimerRef = React.useRef(null);
  const open = Boolean(anchorEl);

  React.useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const handleClick = (event) => {
    event.stopPropagation();
    if (!email) return;

    navigator.clipboard.writeText(email);
    setAnchorEl(event.currentTarget);

    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 2500);
  };

  const handleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Session email — click to view and copy" arrow>
        <IconButton size="small" onClick={handleClick} sx={iconBtnSx}>
          <EmailOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "8px",
              border: 1,
              borderColor: _sp.greenAlt.border,
              backgroundColor: "background.paper",
              boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, maxWidth: 360 }}>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: _sp.greenAlt.color,
              mb: 0.5,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Copied to clipboard
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "text.primary",
              wordBreak: "break-all",
              lineHeight: 1.4,
            }}
          >
            {email}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

function ChatHeader({
  session,
  agentInboxAddress,
  showSessionOperationalMeta = true,
  operatorView = true,
  allowHumanTakeoverControl = false,
  humanTakeoverBusy = false,
  onStartHumanTakeover,
  onEndHumanTakeover,
  leadingActions = null,
  actions = null,
}) {
  const _sp = useStatusPalette();
  const STATUS_CONFIG = buildStatusConfig(_sp);
  const isHumanTakeoverActive = session?.status === STATUS_HUMAN_TAKEOVER;
  const sessionEmail = showSessionOperationalMeta
    ? buildSessionEmailAddress(agentInboxAddress, session.emailThreadToken)
    : null;
  const isPublicShareSession = Boolean(
    operatorView &&
      showSessionOperationalMeta &&
      session?.source === "PUBLIC_CHAT" &&
      session?.publicShareId
  );
  const [shareIdCopied, setShareIdCopied] = React.useState(false);

  const handleCopyShareId = (e) => {
    e.stopPropagation();
    if (!isPublicShareSession) return;
    navigator.clipboard.writeText(session.publicShareId);
    setShareIdCopied(true);
    setTimeout(() => setShareIdCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.subtle",
        gap: 1.5,
        flexShrink: 0,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        ...pageHeaderMinSx,
        flexWrap: "nowrap",
        overflow: "hidden",
      }}
    >
      {leadingActions && (
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          {leadingActions}
        </Box>
      )}
      <Box
        sx={{
          minWidth: 0,
          flexShrink: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <StatusBadge status={session.status} config={STATUS_CONFIG} bare />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          {session.title || "Untitled session"}
        </Typography>
      </Box>
      {isPublicShareSession && (
        <Tooltip
          title={`Share ID: ${session.publicShareId}`}
          placement="top"
          arrow
        >
          <Box
            onClick={handleCopyShareId}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              py: "4px",
              px: "8px",
              borderRadius: "6px",
              maxWidth: 160,
              minWidth: 0,
              flexShrink: 1,
              backgroundColor: shareIdCopied ? _sp.greenAlt.bg : "action.hover",
              border: 1,
              borderColor: shareIdCopied ? _sp.greenAlt.border : "divider",
              cursor: "pointer",
              transition: "all 150ms ease",
              overflow: "hidden",
              "&:hover": {
                backgroundColor: shareIdCopied
                  ? _sp.greenAlt.bg
                  : "action.selected",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: shareIdCopied ? _sp.greenAlt.color : "text.primary",
                lineHeight: 1.4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {shareIdCopied
                ? "Share ID copied"
                : `Shared link · ${formatShareId(session.publicShareId)}`}
            </Typography>
          </Box>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }} />
      <Box
        sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        {sessionEmail && <SessionEmailButton email={sessionEmail} />}
        {allowHumanTakeoverControl && (
          <Tooltip
            title={
              humanTakeoverBusy
                ? "Updating..."
                : isHumanTakeoverActive
                ? "End takeover"
                : "Start takeover"
            }
            arrow
          >
            <IconButton
              size="small"
              disabled={humanTakeoverBusy}
              onClick={() =>
                isHumanTakeoverActive
                  ? onEndHumanTakeover?.()
                  : onStartHumanTakeover?.()
              }
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                border: "1px solid",
                flexShrink: 0,
                ...(isHumanTakeoverActive
                  ? {
                      backgroundColor: _sp.teal.color,
                      borderColor: _sp.teal.color,
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: _sp.teal.color,
                        opacity: 0.85,
                      },
                    }
                  : {
                      borderColor: _sp.teal.border,
                      color: _sp.teal.color,
                      "&:hover": {
                        borderColor: _sp.teal.color,
                        backgroundColor: _sp.teal.bg,
                      },
                    }),
              }}
            >
              <PanToolIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {actions}
      </Box>
    </Box>
  );
}

export default ChatHeader;
